import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { chequeEmitidoInicial, claveBancoCatalogo, formatMoneda } from "./utils";

const FORMATO_FECHA = /^\d{4}-\d{2}-\d{2}$/;

const hoyApi = () => {
  const hoy = new Date();
  const mes = String(hoy.getMonth() + 1).padStart(2, "0");
  const dia = String(hoy.getDate()).padStart(2, "0");
  return `${hoy.getFullYear()}-${mes}-${dia}`;
};

/** True solo si pasan TODOS los campos con * y fechas coherentes. */
export const esChequeEmitidoFormCompleto = (cheque) => {
  if (!cheque || typeof cheque !== "object") return false;

  const nro = String(cheque.nro_cheque ?? "").trim();
  const banco = String(cheque.banco_emisor ?? "").trim();
  const importeRaw = String(cheque.importe ?? "")
    .trim()
    .replace(/\s/g, "")
    .replace(",", ".");
  const importe = Number(importeRaw);
  const emision = String(cheque.fecha_emision ?? "").trim();
  const pago = String(cheque.fecha_pago ?? "").trim();

  if (!nro) return false;
  if (!banco) return false;
  if (importeRaw === "" || !Number.isFinite(importe) || importe <= 0) return false;
  if (!FORMATO_FECHA.test(emision)) return false;
  if (!FORMATO_FECHA.test(pago)) return false;
  if (pago < emision) return false;

  return true;
};

const estadoVacioForm = (restante = 0) => {
  const restanteNum = Number(restante) || 0;
  return {
    ...chequeEmitidoInicial(),
    fecha_emision: hoyApi(),
    fecha_pago: "",
    nro_cheque: "",
    banco_emisor: "",
    codigo_banco: "",
    importe:
      restanteNum > 0 ? String(Math.round(restanteNum * 100) / 100) : "",
  };
};

export default function FormChequeEmitido({
  open,
  onClose,
  onAgregar,
  restante = 0,
  cuentasBancarias = [],
  loadingCuentas = false,
}) {
  const [cheque, setCheque] = useState(() => estadoVacioForm(restante));
  const [error, setError] = useState("");

  const bancosUnicos = useMemo(() => {
    const map = new Map();
    (cuentasBancarias || []).forEach((c) => {
      const key = claveBancoCatalogo(c);
      if (!key || map.has(key)) return;
      map.set(key, {
        codigo: key,
        nombre: c.razon_social || c.codigo || key,
      });
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.nombre).localeCompare(String(b.nombre), "es")
    );
  }, [cuentasBancarias]);

  // Reinicio completo cada vez que se abre el diálogo.
  useEffect(() => {
    if (!open) return;
    setCheque(estadoVacioForm(restante));
    setError("");
  }, [open, restante]);

  const patch = (cambios) => {
    setError("");
    setCheque((prev) => ({ ...prev, ...cambios }));
  };

  const nroOk = String(cheque.nro_cheque ?? "").trim().length > 0;
  const bancoOk = String(cheque.banco_emisor ?? "").trim().length > 0;
  const importeNum = Number(
    String(cheque.importe ?? "")
      .trim()
      .replace(/\s/g, "")
      .replace(",", ".")
  );
  const importeOk = Number.isFinite(importeNum) && importeNum > 0;
  const emisionOk = FORMATO_FECHA.test(String(cheque.fecha_emision ?? "").trim());
  const pagoOk = FORMATO_FECHA.test(String(cheque.fecha_pago ?? "").trim());
  const fechasOrdenOk =
    emisionOk &&
    pagoOk &&
    String(cheque.fecha_pago) >= String(cheque.fecha_emision);

  // Botón Agregar solo si pasan todos los obligatorios (*).
  const puedeAgregar =
    nroOk &&
    bancoOk &&
    importeOk &&
    emisionOk &&
    pagoOk &&
    fechasOrdenOk &&
    !loadingCuentas &&
    bancosUnicos.length > 0;

  const agregar = () => {
    if (!puedeAgregar || !esChequeEmitidoFormCompleto(cheque)) {
      if (!nroOk) {
        setError("Indique el número de cheque");
        return;
      }
      if (!bancoOk) {
        setError("Seleccioná el banco emisor");
        return;
      }
      if (!importeOk) {
        setError("El importe debe ser mayor a 0");
        return;
      }
      if (!emisionOk) {
        setError("Indique la fecha de emisión");
        return;
      }
      if (!pagoOk) {
        setError("Indique la fecha de pago");
        return;
      }
      if (!fechasOrdenOk) {
        setError("La fecha de pago no puede ser anterior a la de emisión");
        return;
      }
      setError("Complete todos los campos obligatorios (*)");
      return;
    }

    onAgregar?.({
      ...cheque,
      nro_cheque: String(cheque.nro_cheque).trim(),
      banco_emisor: String(cheque.banco_emisor).trim(),
      importe: importeNum,
    });
    onClose?.();
  };

  const restanteNum = Number(restante) || 0;
  const fechaPagoInvalida =
    emisionOk && pagoOk && String(cheque.fecha_pago) < String(cheque.fecha_emision);

  const codigoBancoSeleccionado =
    cheque.codigo_banco ||
    bancosUnicos.find((b) => b.nombre === cheque.banco_emisor)?.codigo ||
    "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      // Remount limpio por apertura: evita estado “válido” de un alta anterior.
      key={open ? "cheque-emitido-abierto" : "cheque-emitido-cerrado"}
    >
      <DialogTitle>Cargar cheque emitido</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          El cheque se crea junto con la orden de pago y queda asociado a ella.
          {restanteNum > 0
            ? ` Falta cubrir ${formatMoneda(restanteNum)}.`
            : ""}
        </Typography>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              required
              label="N° de cheque"
              value={cheque.nro_cheque ?? ""}
              onChange={(e) =>
                patch({ nro_cheque: e.target.value.slice(0, 30) })
              }
              error={open && !nroOk && Boolean(error)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              required
              label="Importe"
              type="number"
              value={cheque.importe ?? ""}
              onChange={(e) => patch({ importe: e.target.value })}
              onWheel={(e) => e.target.blur()}
              inputProps={{ min: 0, step: 0.01 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
              error={open && !importeOk && Boolean(error)}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl
              fullWidth
              size="small"
              required
              error={open && !bancoOk && Boolean(error)}
              disabled={loadingCuentas || bancosUnicos.length === 0}
            >
              <InputLabel id="cheque-banco-emisor-label">Banco emisor</InputLabel>
              <Select
                labelId="cheque-banco-emisor-label"
                label="Banco emisor"
                value={codigoBancoSeleccionado}
                onChange={(e) => {
                  const key = e.target.value;
                  const banco = bancosUnicos.find((b) => b.codigo === key);
                  patch({
                    codigo_banco: key,
                    banco_emisor: banco?.nombre || "",
                  });
                }}
              >
                {bancosUnicos.map((banco) => (
                  <MenuItem key={banco.codigo} value={banco.codigo}>
                    {banco.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {loadingCuentas ? (
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="caption" color="text.secondary">
                  Cargando bancos de la empresa…
                </Typography>
              </Box>
            </Grid>
          ) : null}
          {!loadingCuentas && bancosUnicos.length === 0 ? (
            <Grid item xs={12}>
              <Alert severity="warning">
                No hay bancos/cuentas activos de la empresa para seleccionar.
              </Alert>
            </Grid>
          ) : null}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              required
              label="Fecha de emisión"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={cheque.fecha_emision ?? ""}
              onChange={(e) => patch({ fecha_emision: e.target.value })}
              error={open && !emisionOk && Boolean(error)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              required
              label="Fecha de pago"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={cheque.fecha_pago ?? ""}
              onChange={(e) => patch({ fecha_pago: e.target.value })}
              error={fechaPagoInvalida || (open && !pagoOk && Boolean(error))}
              helperText={
                fechaPagoInvalida
                  ? "No puede ser anterior a la emisión"
                  : undefined
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Cláusula (opcional)"
              value={cheque.clausula ?? ""}
              onChange={(e) =>
                patch({ clausula: e.target.value.slice(0, 60) })
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={Number(cheque.electronico) === 1}
                  onChange={(e) =>
                    patch({ electronico: e.target.checked ? 1 : 0 })
                  }
                />
              }
              label="E-cheque"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          type="button"
          variant="contained"
          onClick={agregar}
          disabled={!puedeAgregar}
          sx={{
            ...(puedeAgregar
              ? {}
              : {
                  "&.Mui-disabled": {
                    bgcolor: "action.disabledBackground",
                    color: "action.disabled",
                  },
                }),
          }}
        >
          Agregar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
